/**
 * useOfflineQueue - React hook for offline queue management
 * 
 * Provides:
 * - Queue status monitoring
 * - Auto-sync on reconnect
 * - Manual sync trigger
 * - Queue statistics
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import db, { 
  syncAll, 
  getQueueStats, 
  getPendingItems,
  retryItem,
  deleteItem,
  clearUploaded,
  QUEUE_STATUS 
} from './offlineQueue';

export const useOfflineQueue = (api) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncResult, setLastSyncResult] = useState(null);
  const syncInProgress = useRef(false);
  
  // Live query for queue stats
  const stats = useLiveQuery(async () => {
    return await getQueueStats();
  }, [], { photos: { total: 0 }, incidents: { total: 0 }, totalPending: 0, totalFailed: 0 });
  
  // Live query for pending items
  const pendingItems = useLiveQuery(async () => {
    return await getPendingItems();
  }, [], { photos: [], incidents: [], total: 0 });
  
  // Perform sync
  const performSync = useCallback(async () => {
    if (!api || syncInProgress.current || !navigator.onLine) return;
    
    syncInProgress.current = true;
    setIsSyncing(true);
    
    try {
      const result = await syncAll(api);
      setLastSyncResult({
        ...result,
        timestamp: new Date().toISOString(),
        success: true
      });
      
      // Clean up old uploaded items after successful sync
      if (result.totalSynced > 0) {
        setTimeout(() => clearUploaded(), 5000);
      }
      
      return result;
    } catch (error) {
      console.error('[useOfflineQueue] Sync error:', error);
      setLastSyncResult({
        error: error.message,
        timestamp: new Date().toISOString(),
        success: false
      });
    } finally {
      setIsSyncing(false);
      syncInProgress.current = false;
    }
  }, [api]);
  
  // Handle online/offline events
  useEffect(() => {
    const handleOnline = () => {
      console.log('[useOfflineQueue] Back online - triggering sync');
      setIsOnline(true);
      // Wait a moment for connection to stabilize
      setTimeout(() => performSync(), 1000);
    };
    
    const handleOffline = () => {
      console.log('[useOfflineQueue] Went offline');
      setIsOnline(false);
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [performSync]);
  
  // Initial sync on mount if online and has pending items
  useEffect(() => {
    if (isOnline && stats?.totalPending > 0) {
      performSync();
    }
  }, [isOnline, stats?.totalPending, performSync]);
  
  // Periodic sync attempt (every 30 seconds if pending items exist)
  useEffect(() => {
    const interval = setInterval(() => {
      if (isOnline && stats?.totalPending > 0 && !isSyncing) {
        performSync();
      }
    }, 30000);
    
    return () => clearInterval(interval);
  }, [isOnline, stats?.totalPending, isSyncing, performSync]);
  
  // Manual retry function
  const retry = useCallback(async (type, id) => {
    await retryItem(type, id);
    if (isOnline) {
      performSync();
    }
  }, [isOnline, performSync]);
  
  // Manual delete function
  const remove = useCallback(async (type, id) => {
    await deleteItem(type, id);
  }, []);
  
  // Manual sync trigger
  const triggerSync = useCallback(() => {
    return performSync();
  }, [performSync]);
  
  return {
    isOnline,
    isSyncing,
    stats,
    pendingItems,
    lastSyncResult,
    triggerSync,
    retry,
    remove,
    hasPending: stats?.totalPending > 0,
    hasFailed: stats?.totalFailed > 0
  };
};

export { QUEUE_STATUS };
export default useOfflineQueue;
